import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";

import { motion } from "framer-motion";
import Footer from "../components/Footer";
import toast from "react-hot-toast";
import { RefreshCw } from "lucide-react";

const API_URL = "https://axtelica-api.vercel.app";

const ContactPage = () => {
  // ================= FORM =================
  const [form, setForm] = useState({
    email: "",
    firstName: "",
    lastName: "",
    company: "",
    phone: "",
    department: "",
    message: "",
  });

  // ================= STATES =================
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] =
    useState("");

  const [loading, setLoading] =
    useState(false);

  // ================= CAPTCHA =================
  const [captchaImage, setCaptchaImage] =
    useState("");

  const [captchaAnswer, setCaptchaAnswer] =
    useState("");

  const [captchaLoading, setCaptchaLoading] =
    useState(false);

  // ✅ IMPORTANT FIX
  // prevents stale token issue
  const captchaTokenRef = useRef("");

  // ================= FETCH CAPTCHA =================
  const fetchCaptcha = useCallback(async () => {
    try {
      setCaptchaLoading(true);

      // clear old captcha
      setCaptchaAnswer("");

      const res = await fetch(
        `${API_URL}/api/captcha`,
        {
          method: "GET",
          cache: "no-store",
        }
      );

      const data = await res.json();

      if (data.success) {
        setCaptchaImage(data.captcha_image);

        // ✅ always latest token
        captchaTokenRef.current =
          data.captcha_token;
      } else {
        toast.error("Failed to load CAPTCHA");
      }

    } catch (err) {
      console.error(err);
      toast.error("Server error");
    } finally {
      setCaptchaLoading(false);
    }
  }, []);

  // ================= LOAD CAPTCHA =================
  useEffect(() => {
    fetchCaptcha();
  }, [fetchCaptcha]);

  // ================= HANDLE CHANGE =================
  const handleChange = (e) => {
    setForm({
      ...form,
      [e.target.name]: e.target.value,
    });

    setErrors((prev) => ({
      ...prev,
      [e.target.name]: "",
      global: "",
    }));

    setGlobalError("");
  };

  // ================= VALIDATION =================
  const validate = () => {
    let err = {};

    if (!form.email.trim()) {
      err.email = "Email is required";

    } else if (
      !/^\S+@\S+\.\S+$/.test(form.email)
    ) {
      err.email = "Invalid email format";
    }

    if (!form.firstName.trim()) {
      err.firstName =
        "First name is required";
    }

    if (!form.lastName.trim()) {
      err.lastName =
        "Last name is required";
    }

    if (!form.company.trim()) {
      err.company =
        "Company is required";
    }

    if (!form.phone.trim()) {
      err.phone = "Phone is required";

    } else if (
      !/^\d{10}$/.test(form.phone)
    ) {
      err.phone =
        "Phone must be 10 digits";
    }

    if (!form.department.trim()) {
      err.department =
        "Select a department";
    }

    if (!form.message.trim()) {
      err.message =
        "Message is required";
    }

    // CAPTCHA
    if (!captchaAnswer.trim()) {
      err.captcha =
        "Please enter CAPTCHA";
    }

    return err;
  };

  // ================= SUBMIT =================
  const handleSubmit = async (e) => {
    e.preventDefault();

    setErrors({});
    setGlobalError("");

    const validationErrors = validate();

    if (
      Object.keys(validationErrors)
        .length > 0
    ) {
      setErrors(validationErrors);

      toast.error(
        "Please fill all fields correctly"
      );

      return;
    }

    // ✅ latest token
    const currentCaptchaToken =
      captchaTokenRef.current;

    if (!currentCaptchaToken) {
      toast.error(
        "CAPTCHA expired. Please refresh."
      );

      fetchCaptcha();
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...form,

        // ✅ IMPORTANT FIX
        captchaAnswer: captchaAnswer
          .trim()
          .toUpperCase(),

        captchaToken:
          currentCaptchaToken,
      };

      const res = await fetch(
        `${API_URL}/api/contact`,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",
          },

          body: JSON.stringify(payload),
        }
      );

      const data = await res.json();

      // ================= CAPTCHA FAIL =================
      if (!res.ok) {

        if (
          data.error
            ?.toLowerCase()
            .includes("captcha")
        ) {
          // refresh captcha automatically
          fetchCaptcha();
        }

        throw new Error(
          data.error ||
            "Failed to send message"
        );
      }

      // ================= SUCCESS =================
      toast.success(
        "Message sent successfully!"
      );

      // RESET
      setForm({
        email: "",
        firstName: "",
        lastName: "",
        company: "",
        phone: "",
        department: "",
        message: "",
      });

      setCaptchaAnswer("");

      setErrors({});

      setGlobalError("");

      // new captcha
      fetchCaptcha();

    } catch (error) {
      console.error(error);

      toast.error(
        error.message || "Server error"
      );

    } finally {
      setLoading(false);
    }
  };

  return (
    <div>

      <section className="min-h-screen bg-[#0e1231] py-32 sm:py-40 px-4">

        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center">

          {/* LEFT CONTENT */}
          <motion.div
            initial={{
              opacity: 0,
              x: -40,
            }}
            animate={{
              opacity: 1,
              x: 0,
            }}
            className="text-black"
          >

            <h1
              className="text-4xl md:text-5xl font-bold mb-6 
              bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 
              bg-clip-text text-transparent"
            >
              Get in Touch
            </h1>

            <p className="text-gray-300 text-lg leading-relaxed max-w-md mb-6">
              Have questions or want to
              explore how we can help your
              business? Fill out the form
              and our team will get back
              to you.
            </p>

            <p className="text-gray-400 leading-relaxed mb-6 max-w-lg border-l-2 border-pink-500 pl-4">
              We welcome any questions
              you may have or further
              discussions about how our
              services and solutions can
              meet your needs.
            </p>

            <h3 className="text-white text-xl font-semibold mb-3">
              Contact Us
            </h3>

            <p className="text-gray-400 leading-relaxed mb-6 max-w-lg">
              Schedule a consultation,
              request a demo, or chat
              with our AI assistant.
            </p>

            <p className="text-gray-400 mb-2">
              EMAIL OUR GLOBAL TEAM AT
            </p>

            <a
              href="mailto:info@axtelica.com"
              className="text-pink-400 font-medium text-lg hover:underline"
            >
              info@axtelica.com
            </a>

          </motion.div>

          {/* FORM */}
          <motion.form
            onSubmit={handleSubmit}
            initial={{
              opacity: 0,
              x: 40,
            }}
            animate={{
              opacity: 1,
              x: 0,
            }}
            className="bg-white rounded-3xl p-6 sm:p-8 space-y-4 text-black shadow-2xl"
          >

            {/* EMAIL */}
            <div>

              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="Business Email *"
                className={`w-full border px-4 py-3 rounded-xl outline-none ${
                  errors.email
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              />

              {errors.email && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.email}
                </p>
              )}

            </div>

            {/* FIRST + LAST */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

              <div>

                <input
                  name="firstName"
                  value={form.firstName}
                  onChange={handleChange}
                  placeholder="First Name *"
                  className={`w-full border px-4 py-3 rounded-xl outline-none ${
                    errors.firstName
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />

                {errors.firstName && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.firstName}
                  </p>
                )}

              </div>

              <div>

                <input
                  name="lastName"
                  value={form.lastName}
                  onChange={handleChange}
                  placeholder="Last Name *"
                  className={`w-full border px-4 py-3 rounded-xl outline-none ${
                    errors.lastName
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                />

                {errors.lastName && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.lastName}
                  </p>
                )}

              </div>

            </div>

            {/* COMPANY */}
            <div>

              <input
                name="company"
                value={form.company}
                onChange={handleChange}
                placeholder="Company *"
                className={`w-full border px-4 py-3 rounded-xl outline-none ${
                  errors.company
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              />

              {errors.company && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.company}
                </p>
              )}

            </div>

            {/* PHONE */}
            <div>

              <input
                name="phone"
                value={form.phone}
                onChange={(e) => {
                  const val =
                    e.target.value.replace(
                      /\D/g,
                      ""
                    );

                  if (val.length <= 10) {
                    setForm({
                      ...form,
                      phone: val,
                    });
                  }

                  setErrors((prev) => ({
                    ...prev,
                    phone: "",
                  }));
                }}
                placeholder="Phone Number *"
                className={`w-full border px-4 py-3 rounded-xl outline-none ${
                  errors.phone
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              />

              {errors.phone && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.phone}
                </p>
              )}

            </div>

            {/* DEPARTMENT */}
            <div>

              <select
                name="department"
                value={form.department}
                onChange={handleChange}
                className={`w-full border px-4 py-3 rounded-xl outline-none bg-white ${
                  errors.department
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              >

                <option value="">
                  Select Department *
                </option>

                <option value="Sales">
                  Sales
                </option>

                <option value="Support">
                  Support
                </option>

                <option value="Marketing">
                  Marketing
                </option>

                <option value="Other">
                  Other
                </option>

              </select>

              {errors.department && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.department}
                </p>
              )}

            </div>

            {/* MESSAGE */}
            <div>

              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                rows="4"
                placeholder="Message *"
                className={`w-full border px-4 py-3 rounded-xl outline-none resize-none ${
                  errors.message
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              />

              {errors.message && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.message}
                </p>
              )}

            </div>

            {/* CAPTCHA */}
            <div>

              <label className="text-sm font-medium mb-2 block">
                Security Code *
              </label>

              <div className="flex items-center gap-3 mb-3">

                {/* CAPTCHA IMAGE */}
                <div
                  className="border border-gray-300 rounded-xl overflow-hidden bg-gray-50 flex items-center justify-center"
                  style={{
                    width: 220,
                    height: 70,
                  }}
                >

                  {captchaLoading ? (
                    <span className="text-gray-400 text-sm">
                      Loading...
                    </span>
                  ) : captchaImage ? (
                    <img
                      src={captchaImage}
                      alt="CAPTCHA"
                      className="w-full h-full object-contain"
                      draggable={false}
                    />
                  ) : (
                    <span className="text-gray-400 text-sm">
                      Failed to load
                    </span>
                  )}

                </div>

                {/* REFRESH */}
                <button
                  type="button"
                  onClick={fetchCaptcha}
                  disabled={captchaLoading}
                  className="w-11 h-11 rounded-full border border-gray-300 flex items-center justify-center hover:border-[#FF3366] hover:text-[#FF3366] transition"
                >

                  <RefreshCw
                    size={16}
                    className={
                      captchaLoading
                        ? "animate-spin"
                        : ""
                    }
                  />

                </button>

              </div>

              {/* CAPTCHA INPUT */}
              <input
                type="text"
                value={captchaAnswer}
                onChange={(e) => {
                  setCaptchaAnswer(
                    e.target.value
                      .toUpperCase()
                      .replace(/\s/g, "")
                  );

                  setErrors((prev) => ({
                    ...prev,
                    captcha: "",
                  }));
                }}
                placeholder="Enter CAPTCHA"
                autoComplete="off"
                maxLength={5}
                className={`w-full border px-4 py-3 rounded-xl uppercase tracking-[4px] outline-none ${
                  errors.captcha
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              />

              {errors.captcha && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.captcha}
                </p>
              )}

            </div>

            {/* SUBMIT */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#FF3366] hover:bg-pink-600 transition text-white py-3 rounded-xl font-medium disabled:opacity-50"
            >
              {loading
                ? "Sending..."
                : "Send Message"}
            </button>

            {/* GLOBAL ERROR */}
            {globalError && (
              <p className="text-red-500 text-sm text-center">
                {globalError}
              </p>
            )}

          </motion.form>

        </div>

      </section>

      <Footer />

    </div>
  );
};

export default ContactPage;